import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip,
  Radar, RadarChart, PolarAngleAxis, PolarGrid } from "recharts";
import { CalendarDays, Target, FileText, ArrowRight, Activity, Sparkles, Lock, Package, ClipboardCheck, XCircle, CreditCard, ShieldCheck, Mail, CalendarClock, Clock, Trophy, Award, Zap, User as UserIcon, MapPin } from "lucide-react";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import { useProgressView } from "@/context/ProgressViewContext";
import SportSwitcher from "@/components/SportSwitcher";
import PlayerLevelCard from "@/components/PlayerLevelCard";
import { ClaimPastAssessment } from "@/components/AssessmentClaimsWidgets";
import SkillSnapshotRadar from "@/components/SkillSnapshotRadar";
import StudentSpiderTimeline from "@/components/StudentSpiderTimeline";
import MilestonesRoadmap from "@/components/MilestonesRoadmap";
import AchievementBadges from "@/components/AchievementBadges";
import PerformanceAthleteCard from "@/components/PerformanceAthleteCard";
import EditorialProgressMap from "@/components/EditorialProgressMap";
import SimpleLauncher from "@/components/SimpleLauncher";
import { LookingForSession } from "@/components/LookingForSession";
import { DuprPrompt } from "@/components/DuprPrompt";
import { useUserMode } from "@/context/UserModeContext";
import { PayNowButton, UnpaidFlag, isUnpaid } from "@/components/PayNowButton";

export default function StudentDashboard() {
  const { user, setUser } = useAuth();
  const { sport, skills, meta, multipleSports, enabledSports } = useSport();
  const { view: progressView } = useProgressView();
  const { isSimple } = useUserMode();
  const [data, setData] = useState(null);
  const [synergy, setSynergy] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pendingPacks, setPendingPacks] = useState([]);
  const [payingPkg, setPayingPkg] = useState(null);
  const [decliningPkg, setDecliningPkg] = useState(null);
  // Collapse-when-many toggles — keeps the dashboard scannable when a student
  // holds a large roster of packs / has stacked pending offers.
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAllActive, setShowAllActive] = useState(false);
  // Iter94 — generic Store credits summary: total balance + earliest expiry.
  const [credits, setCredits] = useState({ total_remaining: 0, next_expiry: null, packs: [] });
  const PACKS_INITIAL = 3;

  const reload = async () => {
    const r = await api.get(`/dashboard/student?sport=${sport}`);
    setData(r.data);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    api.get(`/dashboard/student?sport=${sport}`).then((r) => setData(r.data));
  }, [sport]);

  useEffect(() => {
    if (!user || !multipleSports) return;
    api.get(`/synergy/${user.id}`).then((r) => setSynergy(r.data));
  }, [user, sport, multipleSports]);

  useEffect(() => {
    api.get("/packages/mine").then((r) => setPackages(r.data || [])).catch(() => {});
    api.get("/packages/pending").then((r) => setPendingPacks(r.data || [])).catch(() => {});
    // Iter94 — live credit balance for the dashboard widget.
    api.get("/me/credits").then((r) => setCredits(r.data || { total_remaining: 0, next_expiry: null, packs: [] })).catch(() => {});
  }, [user]);

  // If the student came back from Stripe checkout (?package_paid=…&session_id=…)
  // poll /checkout/status to flip the pack to paid without waiting for the
  // webhook, then remove it from pending + surface a toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidPkg = params.get("package_paid");
    const sessionId = params.get("session_id");
    const cancelledPkg = params.get("package_cancelled");
    if (cancelledPkg) {
      toast.info("Payment cancelled — you can try again when you're ready.");
      window.history.replaceState({}, "", "/dashboard");
      return;
    }
    if (!paidPkg || !sessionId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data: st } = await api.get(`/checkout/status/${sessionId}`);
        if (st.payment_status === "paid") {
          toast.success("Package payment confirmed — sessions unlocked.");
          const [{ data: mine }, { data: pend }] = await Promise.all([
            api.get("/packages/mine"),
            api.get("/packages/pending"),
          ]);
          setPackages(mine || []);
          setPendingPacks(pend || []);
          window.history.replaceState({}, "", "/dashboard");
          return;
        }
      } catch (_e) { /* keep polling */ }
      if (attempts < 6) setTimeout(tick, 1500);
      else window.history.replaceState({}, "", "/dashboard");
    };
    tick();
  }, []);

  const payPendingPack = async (p) => {
    setPayingPkg(p.id);
    try {
      const { data } = await api.post(`/packages/${p.id}/checkout`, { origin_url: window.location.origin });
      if (data.auto_confirmed) {
        toast.success("Package activated (£0 charge)");
        const [{ data: mine }, { data: pend }] = await Promise.all([
          api.get("/packages/mine"), api.get("/packages/pending"),
        ]);
        setPackages(mine || []);
        setPendingPacks(pend || []);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
    } finally { setPayingPkg(null); }
  };

  const declinePendingPack = async (p) => {
    if (!window.confirm(`Decline the ${p.sessions_total}-session package (£${Math.round(p.price)}) from ${p.coach_name}? This can't be undone.`)) return;
    setDecliningPkg(p.id);
    try {
      await api.post(`/packages/${p.id}/decline`);
      toast.success("Package declined — coach has been notified");
      const { data: pend } = await api.get("/packages/pending");
      setPendingPacks(pend || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not decline");
    } finally { setDecliningPkg(null); }
  };

  if (!data) return <div className="label-mono text-[#A3A3A3]">Loading…</div>;

  // Find the scheduled assessment booking (only one ever exists at a time)
  const upcomingAssessment = (data.upcoming || []).find((b) => b.is_assessment);
  const hoursUntil = (iso) => {
    try { return (new Date(iso).getTime() - Date.now()) / 1000 / 3600; }
    catch (_e) { return 0; }
  };
  const cancelAssessment = async () => {
    if (!upcomingAssessment) return;
    const h = hoursUntil(upcomingAssessment.start);
    if (h < 48) {
      toast.error(`Cancellations need 48+ hours notice — this session starts in ${Math.max(0, Math.round(h))}h.`);
      return;
    }
    if (!window.confirm("Cancel your assessment? You'll be able to book a different slot afterwards.")) return;
    try {
      const { data: res } = await api.post(`/bookings/${upcomingAssessment.id}/cancel`);
      if (res.package_refunded) {
        toast.success("Assessment cancelled — 1 session refunded to your pack");
      } else {
        toast.success("Assessment cancelled — you can book another slot now");
      }
      await Promise.all([reload(), refreshUser()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel");
    }
  };

  const radar = (skills || []).map((s) => ({ skill: s, value: data.latest_ratings?.ratings?.[s] || 0 }));
  const active = data.active_sports || [sport];

  // Short-circuit for unapproved players — the rest of the dashboard is full
  // of CTAs (Start self-rating, Book new session, Browse offerings…) that the
  // backend now blocks with 403. Show a focused holding screen instead so the
  // player has zero dead-end buttons.
  // Iter86 Guest Experience: pending, pending_details AND rejected all land
  // on the holding screen. Pending users see the demo area (radar/trophies/
  // calendar preview) and coach snippets so they can explore while awaiting
  // approval; pending_details keeps the profile-nudge banner; rejected sees
  // the decline notice without demo content. Iter79 non-blocking navigation
  // still applies — pending users can leave the dashboard to reach
  // /offerings, /find-coach, /progress and /onboarding/self-rating (booking
  // is locked at the button level inside those pages).
  const isPendingPlayer = user?.player_status === "pending"
    || user?.player_status === "pending_details"
    || user?.player_status === "rejected";
  if (isPendingPlayer) {
    return <PendingPlayerHoldingScreen user={user} />;
  }

  // Iter76: Simple Mode swaps the full dashboard for the Apple-style tile
  // launcher. Placed AFTER all hook calls to satisfy rules-of-hooks; coaches
  // + admins already receive `isSimple === false` from UserModeContext.
  if (isSimple && user?.role === "student") {
    return <><SimpleLauncher /><DuprPrompt /></>;
  }

  return (
    <div data-testid="student-dashboard" className="space-y-8">
      <DuprPrompt />
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">PLAYER · {user?.name}</div>
          <h1 className="h-display text-5xl tracking-tighter">Stay on track.</h1>
        </div>
        <SportSwitcher />
      </div>

      {/* DUPR-style skill level card (current / target / progress %) */}
      <PlayerLevelCard />

      <LookingForSession />

      {/* Iter94 — Remaining Credits widget. Only rendered when the student
          actually has credits so it doesn't take up hero space for players
          who've never bought a pack. Tapping the card jumps them to the
          Packages tab where they can top up. */}
      {credits.total_remaining > 0 && (
        <Link
          to="/credits"
          className="card-surface p-5 flex items-center gap-4 hover:border-[#CCFF00] transition-colors fade-up"
          data-testid="credits-widget"
          style={{ borderLeft: "3px solid #CCFF00" }}
        >
          <div className="w-12 h-12 flex items-center justify-center" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
            <Zap className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mono text-[#CCFF00]">REMAINING CREDITS</div>
            <div className="h-display text-3xl leading-none mt-0.5" data-testid="credits-total">
              {credits.total_remaining}
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1" data-testid="credits-next-expiry">
              {credits.next_expiry
                ? `Next batch expires ${new Date(credits.next_expiry).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
                : "No expiry set"}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#A3A3A3] shrink-0" />
        </Link>
      )}

      {/* Player approval gate — three states surface here:
          · pending_details → 48h grace period to complete personal details.
            Big orange banner with a live countdown + hard CTA.
          · pending         → profile done, admin queue reviewing them.
          · rejected        → admin denied.
          Any of these hide booking / contact CTAs elsewhere on the page. */}
      {user?.player_status === "pending_details" && (
        <PendingDetailsBanner />
      )}
      {(user?.player_status === "pending" || user?.player_status === "rejected") && (
        <div
          className="card-surface p-6 fade-up d0 border-l-4"
          style={{ borderColor: user?.player_status === "rejected" ? "#FF4500" : "#CCFF00" }}
          data-testid="player-review-banner"
          data-player-status={user?.player_status}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 flex items-center justify-center shrink-0"
              style={{ background: user?.player_status === "rejected" ? "#FF4500" : "#CCFF00" }}
            >
              {user?.player_status === "rejected"
                ? <XCircle strokeWidth={2.5} className="w-5 h-5 text-black" />
                : <ShieldCheck strokeWidth={2.5} className="w-5 h-5 text-black" />}
            </div>
            <div className="flex-1">
              <div className="label-mono mb-1" style={{ color: user?.player_status === "rejected" ? "#FF4500" : "#CCFF00" }}>
                {user?.player_status === "rejected" ? "ACCOUNT DECLINED" : "UNDER REVIEW"}
              </div>
              <h3 className="h-display text-2xl tracking-tight">
                {user?.player_status === "rejected"
                  ? "Your registration wasn't approved."
                  : "Your account is with the admin."}
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
                {user?.player_status === "rejected"
                  ? <>The admin has declined this account. If you think this is a mistake, please email <a className="text-[#FF4500]" href="mailto:support@rally-lab.com">support@rally-lab.com</a>.</>
                  : <>New player accounts get a quick manual review — you&apos;ll be able to book sessions and see coaches as soon as we approve you. This usually takes under 24 hours. You&apos;ll get an email + in-app notification the moment it&apos;s done.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Assessment gate banner — first thing the student sees if they haven't
          completed an assessment yet. Drives them to book (status='required')
          or shows the scheduled session with a 48h-rule cancel button
          (status='scheduled'). Hides entirely once status='completed'.
          Also hidden for rejected accounts — no point offering CTAs they can't act on. */}
      {user?.player_status !== "rejected" && user?.assessment_status !== "completed" && (() => {
        const isScheduled = user?.assessment_status === "scheduled" && upcomingAssessment;
        const accent = isScheduled ? "#CCFF00" : "#FF4500";
        const Icon = isScheduled ? ClipboardCheck : Lock;
        const h = upcomingAssessment ? hoursUntil(upcomingAssessment.start) : 0;
        const canCancel = h >= 48;
        return (
          <div className="card-surface p-6 fade-up d0 border-l-4" style={{ borderColor: accent }} data-testid="assessment-gate-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: accent }}>
                <Icon strokeWidth={2.5} className="w-5 h-5 text-black" />
              </div>
              <div className="flex-1">
                <div className="label-mono mb-1" style={{ color: accent }}>
                  {isScheduled ? "ASSESSMENT SCHEDULED" : "ASSESSMENT REQUIRED"}
                </div>
                <h3 className="h-display text-2xl tracking-tight">
                  {isScheduled
                    ? `${new Date(upcomingAssessment.start).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} — with ${upcomingAssessment.coach_name || "your coach"}`
                    : "Book your initial assessment to unlock all sessions."}
                </h3>
                <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
                  {isScheduled
                    ? <>Other sessions stay locked until your coach has completed your assessment. You can cancel here up to <span className="text-white">48 hours</span> before the session starts — closer than that, message your coach directly.</>
                    : <>A one-off first-session assessment (~60 min) gets you a baseline rating. Until it&apos;s done <span className="text-white">all other bookings stay locked</span>. If your coach gave you a Performance pack flagged &ldquo;covers assessment&rdquo;, you can redeem it instead of paying.</>}
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {isScheduled ? (
                    <>
                      <Link to="/calendar" className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2" style={{ background: accent, color: "#0A0A0A" }} data-testid="assessment-view-cta">
                        View on calendar <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <button
                        onClick={cancelAssessment}
                        disabled={!canCancel}
                        className="px-5 py-2.5 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-[#0A0A0A] inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="cancel-assessment-cta"
                        title={canCancel ? "Cancel and book another slot" : `Cancellations need 48h+ notice — this is in ${Math.max(0, Math.round(h))}h`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        {canCancel ? "Cancel (48h+ window)" : `Locked · starts in ${Math.max(0, Math.round(h))}h`}
                      </button>
                    </>
                  ) : (
                    <Link to="/offerings?assessment=1" className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2" data-testid="assessment-book-cta">
                      Browse assessment slots <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tracking activation banner — surfaces the Performance/1:1 gate */}
      {user?.role === "student" && user?.tracking_active === false && (
        <div className="card-surface p-5 border-l-4 border-[#FF4500] fade-up d1" data-testid="tracking-locked-banner">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#FF4500] flex items-center justify-center shrink-0">
              <Lock strokeWidth={2.5} className="w-5 h-5 text-black" />
            </div>
            <div className="flex-1">
              <div className="label-mono mb-1" style={{ color: "#FF4500" }}>FULL TRACKING LOCKED</div>
              <h3 className="h-display text-2xl tracking-tight">
                Coach-rated graphs &amp; AI reports activate after your first paid 1:1 or Performance session.
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
                Group sessions only show your <span className="text-white">self-rating</span>. Book + attend any <span className="text-[#FF4500] font-bold">Performance</span> or <span className="text-[#CCFF00] font-bold">1:1</span> session and the full skill-radar, history graphs and 4-week AI reports light up automatically.
              </p>
              <Link to="/offerings" data-testid="tracking-unlock-cta" className="btn-volt mt-4 px-5 py-2.5 text-xs inline-flex items-center gap-2">
                Browse Performance &amp; 1:1 <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* HERO: Upcoming bookings — most-important info, placed at the top */}
      <div className="card-surface p-6 fade-up d0 border-l-4" style={{ borderColor: meta.color }} data-testid="upcoming-bookings-hero">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="label-mono mb-1" style={{ color: meta.color }}><CalendarDays className="w-3.5 h-3.5 inline mr-1" /> UPCOMING SESSIONS</div>
            <h2 className="h-display text-3xl tracking-tight">{data.upcoming?.length ? `${data.upcoming.length} session${data.upcoming.length === 1 ? "" : "s"} ahead` : "Nothing on the calendar."}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="hero-cta-group">
            <Link to="/coaches-near-me" data-testid="coaches-near-me-cta" className="border border-[#262626] hover:border-[#CCFF00] text-white px-4 py-3 text-xs inline-flex items-center gap-2 uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5" /> Coaches near me
            </Link>
            <Link to="/offerings" data-testid="hero-book-cta" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2" style={{ background: meta.color, color: "#0A0A0A" }}>
              Book new session <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
        {data.upcoming?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upcoming-grid">
            {data.upcoming.slice(0, 6).map((b) => {
              const d = new Date(b.start);
              const isToday = new Date().toDateString() === d.toDateString();
              return (
                <Link key={b.id} to={`/session/${b.id}`} className="bg-[#0A0A0A] hover:bg-[#141414] border border-[#262626] hover:border-[color:var(--accent)] p-4 transition-colors block"
                      style={{ "--accent": meta.color, ...(isUnpaid(b) ? { borderColor: "#FF4500" } : {}) }} data-testid={`upcoming-${b.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-mono" style={{ color: meta.color }}>{isToday ? "TODAY" : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }).toUpperCase()}</div>
                    {isUnpaid(b) ? <UnpaidFlag testid={`upcoming-unpaid-${b.id}`} /> : <div className="label-mono text-[#A3A3A3]">{b.session_type?.replace("_", " ").toUpperCase()}</div>}
                  </div>
                  <div className="h-display text-2xl tracking-tighter mb-1">{d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-[#A3A3A3] truncate">{b.notes || `${b.duration_min || 60}min · £${b.price || 0}`}</div>
                  {isUnpaid(b) && <PayNowButton bookingId={b.id} price={b.price} className="mt-3" testid={`upcoming-pay-${b.id}`} />}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-[#A3A3A3]">Pick a session from the offerings page to fill up your schedule.</div>
        )}
      </div>

      {/* Iter64: Active milestone (compact) — daily nudge toward the coach-set goal */}
      {user?.id && <MilestonesRoadmap studentId={user.id} sport={sport} compact />}
      {/* Iter153 — Current spider chart + progress timeline. Uses the
          skill-snapshots endpoint so it always reflects the latest snapshot
          captured on post-session feedback submissions. */}
      {user?.id && <StudentSpiderTimeline studentId={user.id} />}

      {/* Iter74 Phase B: view-synced progress card. Simple view surfaces the
          Editorial Progress Map (iter75); Advanced view surfaces the Athlete Card. */}
      {user?.id && (
        progressView === "simple" && sport === "pickleball"
          ? <EditorialProgressMap studentId={user.id} sport={sport} showPrintButton={false} />
          : <PerformanceAthleteCard studentId={user.id} sport={sport} compact />
      )}

      {/* Iter71: Support Badges (compact) — daily glance at trophies */}
      {user?.id && <AchievementBadges studentId={user.id} sport={sport} compact />}

      {/* Pending package offers — coach created a pack awaiting the student's
          accept & pay. Shown above active packs so it's the first thing they see.
          Collapsed to top N when the queue grows past PACKS_INITIAL. */}
      {pendingPacks.length > 0 && (
        <div className="card-surface p-6 fade-up d0 border-l-4 border-[#FF4500]" data-testid="pending-packages">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="label-mono mb-1" style={{ color: "#FF4500" }}><CreditCard className="w-3.5 h-3.5 inline mr-1" /> PENDING OFFERS</div>
              <h2 className="h-display text-3xl tracking-tight">{pendingPacks.length} package{pendingPacks.length === 1 ? "" : "s"} awaiting your review</h2>
              <div className="text-xs text-[#A3A3A3] mt-1">Your coach has offered you a session pack. Pay securely with Stripe or decline.</div>
            </div>
            {pendingPacks.length > PACKS_INITIAL && (
              <button
                onClick={() => setShowAllPending((v) => !v)}
                className="text-xs uppercase tracking-wider text-[#FF4500] hover:opacity-80 inline-flex items-center gap-1"
                data-testid="pending-packages-toggle"
              >
                {showAllPending ? `Show top ${PACKS_INITIAL}` : `Show all ${pendingPacks.length}`} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(showAllPending ? pendingPacks : pendingPacks.slice(0, PACKS_INITIAL)).map((p) => (
              <div key={p.id} className="bg-[#0A0A0A] border border-[#FF4500]/60 p-4 space-y-3" data-testid={`pending-pkg-${p.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="label-mono text-[#A3A3A3] mb-1">{p.type === "monthly" ? "MONTHLY" : "BLOCK"} · {p.sport?.toUpperCase()}</div>
                    <div className="flex items-baseline gap-2">
                      <div className="h-display text-3xl text-[#CCFF00]">{p.sessions_total}</div>
                      <div className="text-xs text-[#A3A3A3] label-mono">SESSIONS</div>
                    </div>
                    <div className="text-[10px] text-[#A3A3A3] mt-1">from {p.coach_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="h-display text-3xl text-white" data-testid={`pending-price-${p.id}`}>£{Math.round(p.price)}</div>
                    <div className="text-[10px] text-[#A3A3A3] label-mono">TOTAL</div>
                  </div>
                </div>

                {/* Auto-expiry countdown — 14 days from creation, informational */}
                {typeof p.days_until_auto_expiry === "number" && p.days_until_auto_expiry >= 0 && (
                  <div
                    className={`text-[10px] uppercase tracking-wider label-mono ${p.days_until_auto_expiry <= 3 ? "text-[#FF4500]" : "text-[#A3A3A3]"}`}
                    data-testid={`pending-auto-expiry-${p.id}`}
                  >
                    {p.days_until_auto_expiry === 0
                      ? "Expires today — decide before end of day"
                      : `Auto-expires in ${p.days_until_auto_expiry} day${p.days_until_auto_expiry === 1 ? "" : "s"}`}
                  </div>
                )}

                {/* Meta: expiry, coach note, refund policy — surfaced only if set */}
                <div className="space-y-1.5 text-[11px] text-[#E5E5E5]">
                  {p.expires_at && (
                    <div className="flex items-start gap-2" data-testid={`pending-expiry-${p.id}`}>
                      <CalendarDays className="w-3 h-3 text-[#A3A3A3] mt-[2px] shrink-0" />
                      <span><span className="text-[#A3A3A3]">Use by:</span> {String(p.expires_at).slice(0, 10)}</span>
                    </div>
                  )}
                  {p.notes && (
                    <div className="flex items-start gap-2" data-testid={`pending-notes-${p.id}`}>
                      <FileText className="w-3 h-3 text-[#A3A3A3] mt-[2px] shrink-0" />
                      <span className="italic">&ldquo;{p.notes}&rdquo;</span>
                    </div>
                  )}
                  {p.refund_policy && (
                    <div className="flex items-start gap-2 text-[10px] text-[#A3A3A3]" data-testid={`pending-refund-${p.id}`}>
                      <ShieldCheck className="w-3 h-3 mt-[2px] shrink-0" />
                      <span><span className="uppercase tracking-wider">Refund:</span> {p.refund_policy}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => payPendingPack(p)}
                    disabled={payingPkg === p.id}
                    className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`pay-pending-${p.id}`}
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    {payingPkg === p.id ? "Opening Stripe…" : `Review & pay £${Math.round(p.price)}`}
                  </button>
                  <button
                    onClick={() => declinePendingPack(p)}
                    disabled={decliningPkg === p.id}
                    className="px-3 py-2 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`decline-pending-${p.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    {decliningPkg === p.id ? "Declining…" : "Decline"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance packages — only when student holds at least one active pack.
          Collapsed to top N when the roster is large. */}
      {packages.length > 0 && (
        <div className="card-surface p-6 fade-up d0 border-l-4 border-[#CCFF00]" data-testid="my-packages">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="label-mono mb-1" style={{ color: "#CCFF00" }}><Package className="w-3.5 h-3.5 inline mr-1" /> PERFORMANCE PACKAGES</div>
              <h2 className="h-display text-3xl tracking-tight">{packages.length} active pack{packages.length === 1 ? "" : "s"}</h2>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {packages.length > PACKS_INITIAL && (
                <button
                  onClick={() => setShowAllActive((v) => !v)}
                  className="text-xs uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1"
                  data-testid="my-packages-toggle"
                >
                  {showAllActive ? `Show top ${PACKS_INITIAL}` : `Show all ${packages.length}`}
                </button>
              )}
              <Link to="/offerings" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1">
                Redeem on Offerings <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(showAllActive ? packages : packages.slice(0, PACKS_INITIAL)).map((p) => {
              const usedPct = p.sessions_total > 0 ? Math.round((p.sessions_used / p.sessions_total) * 100) : 0;
              return (
                <div key={p.id} className="bg-[#0A0A0A] border border-[#262626] p-4" data-testid={`my-pkg-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-mono text-[#A3A3A3]">{p.type === "monthly" ? "MONTHLY" : "BLOCK"} · {p.sport?.toUpperCase()}</div>
                    {p.auto_renew && <div className="label-mono text-[#CCFF00]">AUTO-RENEW</div>}
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <div className="h-display text-3xl text-[#CCFF00]">{p.sessions_remaining}</div>
                    <div className="label-mono text-[#A3A3A3]">/ {p.sessions_total} LEFT</div>
                  </div>
                  <div className="h-1 bg-[#262626] overflow-hidden mb-1">
                    <div className="h-full bg-[#CCFF00]" style={{ width: `${usedPct}%` }} />
                  </div>
                  <div className="text-[10px] text-[#A3A3A3]">Coach: {p.coach_name}{p.expires_at ? ` · Expires ${String(p.expires_at).slice(0,10)}` : ""}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past sessions — quick lookback strip so the player can check what they've attended
          without leaving for the calendar page. */}
      {data.past?.length > 0 && (
        <div className="card-surface p-6 fade-up d0" data-testid="past-bookings-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono mb-1 text-[#A3A3A3]"><CalendarDays className="w-3.5 h-3.5 inline mr-1" /> PAST SESSIONS</div>
              <h2 className="h-display text-2xl tracking-tight">Last {data.past.length} session{data.past.length === 1 ? "" : "s"}</h2>
            </div>
            <Link to="/calendar" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">Full history <ArrowRight className="w-3 h-3 inline" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="past-grid">
            {data.past.map((b) => {
              const d = new Date(b.start);
              const cType = b.session_type === "performance" ? "#FF4500" : b.session_type === "one_on_one" ? "#CCFF00" : "#007AFF";
              return (
                <div key={b.id} className="bg-[#0A0A0A] border border-[#262626] p-4 opacity-80" data-testid={`past-${b.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-mono text-[#A3A3A3]">{d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }).toUpperCase()}</div>
                    <div className="label-mono" style={{ color: cType }}>{b.session_type?.replace("_", " ").toUpperCase()}</div>
                  </div>
                  <div className="h-display text-xl tracking-tighter mb-1 text-[#E5E5E5]">{d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-[#A3A3A3] truncate">{b.notes || `${b.duration_min || 60}min · £${b.price || 0}`}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hero focus card */}
      {(() => {
        const focusText = (data.focus || "").trim();
        const lines = focusText.split("\n").map(l => l.trim()).filter(Boolean);
        // "Queued plan" detection: at least one line matches the exact
        // bullet-pattern our backend writes ("• Title (Nm)" — see
        // server.py:append_drill_to_focus). Hand-typed "• Reminder" lines
        // therefore render as bullets but do NOT flip on the "coach queued
        // this plan" badge, which specifically signals the snapshot flow.
        const QUEUED_LINE_RE = /^•\s.+\s\(\d+m\)/;
        const hasQueuedBullet = lines.some(l => QUEUED_LINE_RE.test(l));
        const isQueuedPlan = hasQueuedBullet;
        return (
          <div className="card-surface p-8 relative overflow-hidden fade-up d1 bg-gradient-to-br from-[#141414] to-[#0A0A0A]" data-testid="focus-hero">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Target strokeWidth={1.5} className="w-4 h-4" style={{ color: meta.color }} />
                  <div className="label-mono" style={{ color: meta.color }}>
                    NEXT {meta.label.toUpperCase()} FOCUS
                  </div>
                  {isQueuedPlan && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border ml-1"
                      style={{ color: meta.color, borderColor: meta.color }}
                      data-testid="focus-queued-badge"
                    >
                      <Mail className="w-2.5 h-2.5" /> Your coach queued this plan
                    </span>
                  )}
                </div>
                {isQueuedPlan ? (
                  <ul className="space-y-2" data-testid="focus-bullets">
                    {lines.map((line, i) => {
                      const isBullet = line.startsWith("•");
                      const content = isBullet ? line.replace(/^•\s*/, "") : line;
                      return (
                        <li
                          key={`${i}-${line}`}
                          className="flex items-start gap-3 text-sm md:text-base text-[#E5E5E5] leading-snug"
                          data-testid={`focus-line-${i}`}
                        >
                          {isBullet ? (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: meta.color }} />
                          ) : (
                            <span className="mt-1 h-2 w-2 shrink-0" />
                          )}
                          <span>{content}</span>
                        </li>
                      );
                    })}
                    {data.focus_coach_id && (
                      <li className="pt-3 flex flex-wrap gap-2">
                        <Link
                          to={`/book/coach/${data.focus_coach_id}`}
                          className="btn-volt inline-flex items-center gap-2 py-2 px-4 text-xs"
                          data-testid="book-next-session-cta"
                        >
                          <CalendarClock className="w-4 h-4" />
                          Book my next session{data.focus_coach_name ? ` with ${data.focus_coach_name}` : ""}
                        </Link>
                        {/* Iter151 — Book Focus Session prefilled with focus
                            areas from the latest AI summary. Iter152 — sits
                            next to a "Bring a Training Partner" button that
                            deep-links to the Group flow with the same focus
                            areas so the player can invite mates. */}
                        {(data.focus_areas_list || []).length > 0 && (
                          <Link
                            to={{
                              pathname: "/group-bookings/new",
                              search: `?type=solo&coach=${data.focus_coach_id}&focus=${encodeURIComponent((data.focus_areas_list || []).slice(0, 2).join(","))}`,
                            }}
                            className="inline-flex items-center gap-2 py-2 px-4 text-xs uppercase tracking-wider border border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-black transition-colors"
                            data-testid="book-focus-session-cta"
                          >
                            <CalendarClock className="w-4 h-4" />
                            Book a Focus Session
                          </Link>
                        )}
                        {(data.focus_areas_list || []).length > 0 && (
                          <Link
                            to={{
                              pathname: "/group-bookings/new",
                              search: `?type=group&coach=${data.focus_coach_id}&focus=${encodeURIComponent((data.focus_areas_list || []).slice(0, 2).join(","))}`,
                            }}
                            className="inline-flex items-center gap-2 py-2 px-4 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors"
                            data-testid="bring-training-partner-cta"
                            title="Invite a partner to a Group session with the same focus areas"
                          >
                            <CalendarClock className="w-4 h-4" />
                            Bring a Training Partner
                          </Link>
                        )}
                      </li>
                    )}
                  </ul>
                ) : (
                  <div className="h-display text-3xl md:text-4xl tracking-tight leading-tight">
                    {focusText || `No ${meta.label.toLowerCase()} focus set yet — your coach will assign one after your next session.`}
                  </div>
                )}
              </div>
              <div className="border-l border-[#262626] pl-6">
                <div className="label-mono mb-2">Activity</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><div className="h-display text-3xl" style={{ color: meta.color }}>{data.sessions_count}</div><div className="label-mono">Sessions</div></div>
                  <div><div className="h-display text-3xl text-[#FF4500]">{data.reports_count}</div><div className="label-mono">Reports</div></div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Claim previous assessment (student's shortcut past the fresh-assessment flow) */}
      <ClaimPastAssessment />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Progress */}
        <div className="card-surface p-6 lg:col-span-2 fade-up d2">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">My {meta.label.toLowerCase()} progression</div>
            <div className="flex items-center gap-2 text-xs" style={{ color: meta.color }}><Activity className="w-3 h-3" /> AVG / 10</div>
          </div>
          <div className="h-60">
            {data.progress?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.progress}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={meta.color} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={meta.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                  <YAxis domain={[0, 10]} tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                  <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0 }} labelStyle={{ color: "#A3A3A3" }} />
                  <Area type="monotone" dataKey="avg" stroke={meta.color} strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[#A3A3A3]">Your progression appears here after your first rated session.</div>
            )}
          </div>
        </div>

        {/* Skills radar — iter52: DUPR-derived from assessment ticks. */}
        <SkillSnapshotRadar
          studentId={user.id}
          color={meta.color}
          title={`${meta.label} skill snapshot`}
          compact
        />
      </div>

      {/* Cross-sport synergy panel — only shows if player is active in both sports */}
      {synergy && (synergy.active_sports || []).length >= 2 && (
        <div className="card-surface p-6 fade-up d3 border-l-2 border-l-[#CCFF00]" data-testid="synergy-panel">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono text-[#CCFF00]">CROSS-SPORT SYNERGY</div>
          </div>
          <h3 className="h-display text-2xl mb-1 tracking-tight">
            Shared skills across Squash & Pickleball
          </h3>
          <p className="text-sm text-[#A3A3A3] mb-6 max-w-2xl">
            These fundamentals carry between both sports. Improvements here compound. <span className="text-white">{synergy.focus_skill && `Currently weakest: ${synergy.focus_skill}.`}</span>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(synergy.shared_skills || []).map((s) => {
              const combined = synergy.combined?.[s] || 0;
              const isFocus = s === synergy.focus_skill;
              return (
                <div key={s} className="border border-[#262626] p-4" style={isFocus ? { borderColor: "#FF4500" } : {}}>
                  <div className="label-mono mb-2" style={isFocus ? { color: "#FF4500" } : {}}>{s}</div>
                  <div className="h-display text-3xl tracking-tighter mb-3" style={{ color: isFocus ? "#FF4500" : "#CCFF00" }}>{combined || "—"}<span className="text-sm text-[#A3A3A3]">/10</span></div>
                  <div className="space-y-1">
                    {(synergy.active_sports || []).map((sp) => (
                      <div key={sp} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5" style={{ background: SPORT_META[sp].color }} />
                          {SPORT_META[sp].label}
                        </span>
                        <span className="font-mono text-[#A3A3A3]">{synergy.by_sport?.[sp]?.[s] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Multi-sport prompt — if squash enabled AND player only active in one */}
      {multipleSports && synergy && (synergy.active_sports || []).length === 1 && (
        <div className="card-surface p-6 fade-up d3 flex items-center justify-between flex-wrap gap-4" data-testid="add-sport-prompt">
          <div>
            <div className="label-mono mb-1 text-[#CCFF00]">UNLOCK CROSS-SPORT SYNERGY</div>
            <div className="text-sm">
              You&apos;re currently tracking <span className="font-semibold">{SPORT_META[synergy.active_sports[0]].label}</span>. Add the other sport to see footwork, volley and mental synergies across both.
            </div>
          </div>
          <Link to="/calendar" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2">
            Book a session <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-6 fade-up d3">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">All bookings &amp; calendar</div>
            <Link to="/calendar" className="text-xs uppercase tracking-wider" style={{ color: meta.color }}>Open calendar <ArrowRight className="w-3 h-3 inline" /></Link>
          </div>
          <div className="text-sm text-[#A3A3A3]">
            <CalendarDays className="w-8 h-8 text-[#262626] mb-2" strokeWidth={1.5} />
            Full schedule, past attendance and cancellations live on the calendar page.
          </div>
        </div>

        <div className="card-surface p-6 fade-up d4">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">Reports</div>
            <Link to="/reports" className="text-xs uppercase tracking-wider" style={{ color: meta.color }}>View all <ArrowRight className="w-3 h-3 inline" /></Link>
          </div>
          <div className="text-sm text-[#A3A3A3]">
            <FileText className="w-8 h-8 text-[#262626] mb-2" strokeWidth={1.5} />
            {data.reports_count > 0
              ? `You have ${data.reports_count} ${meta.label.toLowerCase()} 4-week report${data.reports_count > 1 ? "s" : ""} available.`
              : `Your 4-week ${meta.label.toLowerCase()} AI report will appear here once your coach generates it.`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Pending-details 48h grace banner ----------
function PendingDetailsBanner() {
  // Iter86: no deadline pressure — the pending_details expiry job was
  // disabled and the copy encourages completion without threatening account
  // removal. The banner still hard-nudges the user to finish so they can
  // book, but it's framed as an unlock, not a countdown.
  return (
    <div
      className="card-surface p-6 fade-up d0 border-l-4"
      style={{ borderColor: "#FF4500" }}
      data-testid="pending-details-banner"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 flex items-center justify-center shrink-0"
          style={{ background: "#FF4500" }}
        >
          <Clock strokeWidth={2.5} className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-mono mb-1" style={{ color: "#FF4500" }}>
            FINISH YOUR PROFILE
          </div>
          <h3 className="h-display text-2xl tracking-tight">
            One more step to unlock booking.
          </h3>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
            You can browse coaches and demo content right now, but you can&apos;t book or contact anyone until you&apos;ve given us your DOB, postcode and phone. Takes about a minute.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/profile/personal"
              className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2"
              data-testid="finish-profile-cta"
            >
              Finish profile now <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Pending / rejected player holding screen ----------
// A pending player has zero actions they can take beyond finishing their
// personal details or waiting for approval — so the normal STAY-ON-TRACK
// dashboard (self-rating card, booking CTAs, offerings…) all dead-end at
// the ProtectedRoute redirect. This screen gives them a clean summary of
// where they are in the pipeline and only the buttons that actually work.
// ---------- Demo pieces for the pending holding screen (iter86) ----------
// Static, deliberately non-interactive read-only samples that let a
// pending/pending_details user get a feel for the app without touching real
// data. Each component is intentionally tiny and self-contained; the point
// is atmosphere, not accuracy.

const DEMO_RADAR = [
  { skill: "Serve", current: 3.5 },
  { skill: "Return", current: 3.25 },
  { skill: "Volley", current: 3.75 },
  { skill: "Dinks", current: 3.0 },
  { skill: "Third shot", current: 3.5 },
  { skill: "Footwork", current: 3.75 },
];

function DemoSkillsRadar() {
  return (
    <div className="card-surface p-5 fade-up d1 relative overflow-hidden" data-testid="demo-skills-radar">
      <div className="absolute top-3 right-3 label-mono text-[10px] px-1.5 py-0.5 border border-[#525252] text-[#A3A3A3]">DEMO</div>
      <div className="label-mono mb-2" style={{ color: "#A3A3A3" }}>YOUR SKILLS RADAR</div>
      <h3 className="text-lg mb-1">A snapshot of every session.</h3>
      <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed">
        After your first assessment your radar fills with your real levels across serve, dinks, footwork and more. This is what you&apos;re working towards.
      </p>
      <div style={{ width: "100%", height: 220 }} aria-hidden>
        <ResponsiveContainer>
          <RadarChart data={DEMO_RADAR} outerRadius="70%">
            <PolarGrid stroke="#262626" />
            <PolarAngleAxis dataKey="skill" tick={{ fill: "#A3A3A3", fontSize: 10 }} />
            <Radar dataKey="current" stroke="#CCFF00" fill="#CCFF00" fillOpacity={0.25} isAnimationActive={false} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const DEMO_TROPHIES = [
  { name: "First serve", desc: "Nailed your first competent-tick", icon: Zap, color: "#CCFF00" },
  { name: "Level 3.0", desc: "Unlocked when your radar hits 3.0 avg", icon: Trophy, color: "#FFB020" },
  { name: "Consistency streak", desc: "5 sessions in 4 weeks", icon: Award, color: "#A3A3A3" },
];

function DemoTrophies() {
  return (
    <div className="card-surface p-5 fade-up d2 relative overflow-hidden" data-testid="demo-trophies">
      <div className="absolute top-3 right-3 label-mono text-[10px] px-1.5 py-0.5 border border-[#525252] text-[#A3A3A3]">DEMO</div>
      <div className="label-mono mb-2" style={{ color: "#A3A3A3" }}>TROPHY CASE</div>
      <h3 className="text-lg mb-1">Earn badges as you level up.</h3>
      <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed">
        Every level-up and streak fills your case. A quick receipt of the work you&apos;ve put in.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {DEMO_TROPHIES.map(({ name, desc, icon: Icon, color }) => (
          <div key={name} className="border border-[#262626] p-2 opacity-70">
            <Icon strokeWidth={1.75} className="w-5 h-5 mb-1.5" style={{ color }} />
            <div className="text-[11px] font-semibold leading-tight">{name}</div>
            <div className="text-[9px] uppercase tracking-wider text-[#525252] mt-1 leading-tight">{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEMO_CAL = [
  { d: "Mon", time: "6:30 pm", who: "Alex · 1:1" },
  { d: "Wed", time: "7:00 pm", who: "Group · Mixed" },
  { d: "Sat", time: "10:00 am", who: "Sam · 1:1" },
];

function DemoCalendarPreview() {
  return (
    <div className="card-surface p-5 fade-up d3 relative overflow-hidden" data-testid="demo-calendar-preview">
      <div className="absolute top-3 right-3 label-mono text-[10px] px-1.5 py-0.5 border border-[#525252] text-[#A3A3A3]">DEMO</div>
      <div className="label-mono mb-2" style={{ color: "#A3A3A3" }}>UPCOMING SESSIONS</div>
      <h3 className="text-lg mb-1">Your week at a glance.</h3>
      <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed">
        Booked sessions land here. Cancellations, coach notes and pre-session focus all show up on this timeline.
      </p>
      <div className="space-y-1.5">
        {DEMO_CAL.map((s) => (
          <div key={`${s.d}-${s.time}`} className="border border-[#262626] p-2.5 flex items-center gap-3 opacity-70">
            <div className="w-10 text-center">
              <div className="text-[9px] uppercase tracking-wider text-[#525252]">{s.d}</div>
              <div className="text-[11px] text-[#A3A3A3]">{s.time}</div>
            </div>
            <div className="flex-1 text-[11px] text-[#E5E5E5]">{s.who}</div>
            <button
              type="button" disabled
              className="text-[9px] uppercase tracking-wider px-2 py-1 border border-[#262626] text-[#525252] cursor-not-allowed inline-flex items-center gap-1"
              data-testid="demo-book-locked"
              title="Booking unlocks once your account is approved"
            >
              <Lock className="w-2.5 h-2.5" /> Locked
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoachSnippetsList() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/coach-snippets?limit=6")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);
  if (items === null) {
    return <div className="text-xs text-[#525252] label-mono">Loading coaches…</div>;
  }
  if (items.length === 0) {
    return <div className="text-xs text-[#A3A3A3] label-mono">No coaches to preview yet — check back once your account is approved.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coach-snippets-list">
      {items.map((c) => {
        const url = c.avatar_status === "approved" ? buildAvatarUrl(c.avatar_path) : null;
        return (
          <div key={c.id} className="border border-[#262626] p-3 flex gap-3" data-testid={`coach-snippet-${c.id}`}>
            <div className="w-14 h-14 rounded-full overflow-hidden border border-[#262626] bg-[#141414] flex items-center justify-center shrink-0">
              {url
                ? <img src={url} alt={c.first_name} className="w-full h-full object-cover" />
                : <UserIcon className="w-5 h-5 text-[#525252]" strokeWidth={1.5} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{c.first_name || "Coach"}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5 mb-1">
                {(c.coach_sports || []).join(" · ") || "Pickleball"}
              </div>
              {c.short_bio && (
                <div className="text-[11px] text-[#A3A3A3] leading-snug line-clamp-3">{c.short_bio}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PendingPlayerHoldingScreen({ user }) {
  const status = user?.player_status;
  const isRejected = status === "rejected";
  const isPendingDetails = status === "pending_details";
  const accent = isRejected ? "#FF4500" : "#CCFF00";

  return (
    <div data-testid="pending-player-holding-screen" className="space-y-6">
      <div className="fade-up">
        <div className="label-mono mb-2">PLAYER · {user?.name}</div>
        <h1 className="h-display text-5xl tracking-tighter">Almost there.</h1>
      </div>

      {isPendingDetails && (
        <PendingDetailsBanner />
      )}

      {(status === "pending" || isRejected) && (
        <div
          className="card-surface p-6 fade-up d0 border-l-4"
          style={{ borderColor: accent }}
          data-testid="player-review-banner"
          data-player-status={status}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 flex items-center justify-center shrink-0"
              style={{ background: accent }}
            >
              {isRejected
                ? <XCircle strokeWidth={2.5} className="w-5 h-5 text-black" />
                : <ShieldCheck strokeWidth={2.5} className="w-5 h-5 text-black" />}
            </div>
            <div className="flex-1">
              <div className="label-mono mb-1" style={{ color: accent }}>
                {isRejected ? "ACCOUNT DECLINED" : "UNDER REVIEW"}
              </div>
              <h3 className="h-display text-2xl tracking-tight">
                {isRejected
                  ? "Your registration wasn't approved."
                  : "Your account is with the admin."}
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">
                {isRejected
                  ? <>The admin has declined this account. If you think this is a mistake, please email <a className="text-[#FF4500]" href="mailto:support@rally-lab.com">support@rally-lab.com</a>.</>
                  : <>New player accounts get a quick manual review — usually under 24 hours. You&apos;ll get an email + in-app notification the moment you&apos;re approved. Meanwhile, take a look around below — everything you see here is a demo of what unlocks once you&apos;re in.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Demo playground — read-only slice of what the full app looks like.
          Booking is visibly locked so nobody thinks they can act on it. */}
      {!isRejected && (
        <>
          <section className="fade-up d1" data-testid="demo-area">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="label-mono" style={{ color: "#CCFF00" }}>PREVIEW</div>
                <h2 className="h-display text-3xl tracking-tight">Have a look around.</h2>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[#525252] hidden sm:inline">All demo data · unlocks after approval</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <DemoSkillsRadar />
              <DemoTrophies />
              <DemoCalendarPreview />
            </div>
          </section>

          <section className="card-surface p-6 fade-up d2" data-testid="coach-snippets-section">
            <div className="label-mono mb-2" style={{ color: "#A3A3A3" }}>YOUR COACHES</div>
            <h3 className="h-display text-2xl tracking-tight mb-1">Faces you&apos;ll see on court.</h3>
            <p className="text-xs text-[#A3A3A3] mb-4 leading-relaxed max-w-2xl">
              A preview of coaches in the community. Full profiles, availability and pricing appear once your account is approved.
            </p>
            <CoachSnippetsList />
          </section>
        </>
      )}

      <section className="card-surface p-6 fade-up d3" data-testid="pending-next-steps">
        <div className="label-mono mb-3" style={{ color: "#A3A3A3" }}>WHAT HAPPENS NEXT</div>
        <ol className="space-y-3 text-sm text-[#E5E5E5]">
          <li className="flex gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-[#141414] border border-[#262626] text-[10px] shrink-0" style={{ color: isPendingDetails ? "#FF4500" : "#525252" }}>1</span>
            <span className={isPendingDetails ? "" : "line-through text-[#525252]"}>
              Complete your personal details (DOB, postcode, phone).
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-[#141414] border border-[#262626] text-[10px] shrink-0" style={{ color: status === "pending" ? "#CCFF00" : "#525252" }}>2</span>
            <span className={status === "pending" ? "" : "text-[#525252]"}>
              Wait for admin approval (usually under 24 hours).
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-[#141414] border border-[#262626] text-[10px] shrink-0 text-[#525252]">3</span>
            <span className="text-[#525252]">
              Once approved, complete your DUPR-style self-assessment — this pins your starting level so a coach can plan your first session.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-[#141414] border border-[#262626] text-[10px] shrink-0 text-[#525252]">4</span>
            <span className="text-[#525252]">
              Book your first assessment session with any coach in your region.
            </span>
          </li>
        </ol>
      </section>
    </div>
  );
}
